<template>
  <v-form ref="form" v-model="formValid" class="mb-16" @submit.prevent="handleFormSubmit">
    <div class="d-flex w-100" style="position: relative">
      <div class="back-btn-container">
        <v-btn
          class="font-weight-bold pa-0"
          style="min-width: 30px"
          icon
          @click="$store.dispatch('actionForm/changeToFirstPanel')"
        >
          <v-icon>
            {{ icons.mdiArrowLeft }}
          </v-icon>
        </v-btn>
      </div>
    </div>
    <div class="d-flex flex-column align-center px-10">
      <span class="text-h5 text-uppercase">
        {{ $t('action.panels.FUNDRAISER_PERSONAL.heading', locale) }}
      </span>
      <div class="d-flex flex-column align-center w-100">
        <v-btn
          class="font-weight-bold mb-5"
          color="allout-blue"
          text
          small
          @click="$store.dispatch('actionForm/changeToFirstPanel')"
        >
          {{
            $t(
              fundraiserUI.selectedPaymentFrequency === 'one_time'
                ? 'action.panels.FUNDRAISER_PAYMENT.buttons.backToAmount.label.one-time'
                : 'action.panels.FUNDRAISER_PAYMENT.buttons.backToAmount.label.monthly',
              locale,
              { symbol: currentCurrencyConfig.symbol, amount: fundraiserUI.amount }
            )
          }}
        </v-btn>
        <v-btn-toggle
          v-if="paypalEnabled"
          class="w-100 mb-10"
          :value="fundraiserUI.paymentTypeIndex"
          mandatory
          @change="handlePaymentTypeChange"
        >
          <v-btn
            color="allout-blue"
            :class="`font-weight-regular flex-grow-1 ${
              fundraiserUI.selectedProvider === 'stripe' ? 'font-weight-bold white--text' : ''
            }`"
            :outlined="fundraiserUI.selectedProvider !== 'stripe'"
            small
          >
            <v-icon :color="fundraiserUI.selectedProvider === 'stripe' ? 'white' : 'allout-blue'">
              {{ icons.mdiCreditCardOutline }}
            </v-icon>
            <v-icon :color="fundraiserUI.selectedProvider === 'stripe' ? 'white' : 'allout-blue'">
              {{ icons.mdiContactlessPaymentCircleOutline }}
            </v-icon>
            &nbsp;
            {{ $t('action.panels.FUNDRAISER_PERSONAL.buttons.stripe.label', locale) }}
          </v-btn>
          <v-btn
            color="allout-blue"
            :class="`font-weight-regular flex-grow-1 ${
              fundraiserUI.selectedProvider === 'paypal' ? 'font-weight-bold white--text' : ''
            }`"
            :outlined="fundraiserUI.selectedProvider !== 'paypal'"
            small
          >
            <div
              :class="`paypal-svg ${fundraiserUI.selectedProvider === 'paypal' ? 'selected' : ''}`"
            >
              <PayPalLogoSVG />
            </div>
            &nbsp;
            {{ $t('action.panels.FUNDRAISER_PERSONAL.buttons.paypal.label', locale) }}
          </v-btn>
        </v-btn-toggle>
        <div class="mb-4 w-100">
          <div class="w-100">
            <section-dynamic-fields />
          </div>
          <div v-if="formGlobalErrors">
            <v-alert
              v-for="error in formGlobalErrors"
              :key="error.code"
              dense
              outlined
              type="error"
              class="mb-1"
            >
              {{ getFormErrorMessage(error) }}
            </v-alert>
          </div>
        </div>
        <section-data-consent v-if="showOptIn" />
        <transition-group tag="section" name="fade" class="w-100 mb-6">
          {{ fundraiserUI.selectedProvider }}
          <v-btn
            v-if="fundraiserUI.selectedProvider === 'stripe'"
            key="checkout"
            type="submit"
            color="secondary"
            class="text-uppercase font-weight-bold w-100"
            :disabled="!formValid"
            large
          >
            {{ $t('action.panels.FUNDRAISER_PERSONAL.buttons.checkout.label', locale) }}
          </v-btn>
          <div
            v-if="paypalEnabled && fundraiserUI.selectedProvider === 'paypal'"
            key="paypal"
            :class="`paypal-wrap ${paypalButtonsEnabled ? '' : 'disabled'}`"
            @click="validateForm"
          >
            <all-out-spinner v-if="!paypalInitialized" :size="45" />
            <pay-pal-buttons
              v-show="paypalInitialized"
              :locale="locale"
              :amount="fundraiserUI.amount"
              :currency="fundraiserUI.currency"
              :subscription="fundraiserUI.selectedPaymentFrequency == 'monthly'"
              :tax-deductible="formConfig && formConfig.is_tax_deductible"
              :on-init="paypalOnInit"
              :create-order="paypalCreateOrder"
              :create-subscription="paypalCreateSubscription"
              :on-approve="paypalOnApprove"
              :on-click="paypalOnClick"
            />
          </div>
        </transition-group>
        <section-disclaimer />
      </div>
    </div>
  </v-form>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { mdiCreditCardOutline, mdiContactlessPaymentCircleOutline, mdiArrowLeft } from '@mdi/js'

import PayPalButtons from '../ui/PayPalButtons'
import PayPalLogoSVG from '../../assets/images/logos/paypal.svg?inline'
import SectionDynamicFields from './sections/SectionDynamicFields'
import SectionDataConsent from './sections/SectionDataConsent'
import SectionDisclaimer from './sections/SectionDisclaimer'

import AllOutSpinner from '../../../ui/AllOutSpinner'

export default {
  name: 'PanelFundraiserPersonal',
  components: {
    PayPalLogoSVG,
    SectionDynamicFields,
    SectionDataConsent,
    SectionDisclaimer,
    PayPalButtons,
    AllOutSpinner
  },
  data: vm => ({
    formValid: false,
    stripe: null,
    stripePaymentElement: null,
    paypalActions: null,
    stripePaymentElementValid: false,
    stripeClientSecret: null,
    paypalInitialized: false,
    icons: {
      mdiCreditCardOutline,
      mdiContactlessPaymentCircleOutline,
      mdiArrowLeft
    }
  }),
  computed: {
    paypalButtonsEnabled() {
      return this.formValid && this.paypalInitialized
    },
    ...mapState('actionForm', ['fundraiserUI', 'locale', 'formGlobalErrors', 'formIsDirty', 'formConfig']),
    ...mapGetters('actionForm', [
      'paypalEnabled',
      'currentCurrencyConfig',
      'showOptIn',
      'showSmsOptIn'
    ])
  },
  watch: {
    async formValid(valid) {
      if (this.paypalActions) {
        valid ? this.paypalActions.enable() : this.paypalActions.disable()
      }
    }
  },
  methods: {
    validateForm() {
      this.$refs.form.validate()
    },
    handlePaymentTypeChange(value) {
      this.$store.dispatch('actionForm/updatePaymentMethod', {
        paymentTypeIndex: value
      })
    },
    handleFormSubmit() {
      // Process:
      // 1. Checkout button clicked and form is valid
      // 2. Reach out to Stripe to create a payment intent
      // 3. Store action adds the stripe client secret for the payment intent to form vuex store
      if (this.$refs.form.validate()) {
        switch (this.fundraiserUI.selectedProvider) {
          case 'stripe':
            this.$store.dispatch('actionForm/initPayment')
            this.$store.dispatch('actionForm/changeToNextPanel')
            break
          default:
            throw new Error(`Unhandled payment provider "${this.fundraiserUI.selectedProvider}"`)
        }
      }
      return null
    },
    paypalOnInit(data, actions) {
      // Make the actions interface available to this component
      this.paypalActions = actions
      this.paypalInitialized = true
    },
    async paypalOnApprove(data, actions) {
      const { order } = actions
      let orderData = null

      if (order) {
        // This is neccessary for one-time payments
        orderData = await order.capture()
      }

      this.$store.dispatch('actionForm/handlePayPalApproved', orderData)
    },
    paypalOnClick(data, actions) {
      if (!(this.$refs.form.validate() && this.paypalButtonsEnabled)) {
        return actions.reject()
      }
    },
    async paypalCreateOrder(data, actions) {
      // Testing instructions:
      // https://developer.paypal.com/docs/api-basics/sandbox/
      // Integration instructions:
      // https://developer.paypal.com/docs/checkout/integrate/
      return await this.$store.dispatch('actionForm/commitPayPalPayment')
    },
    async paypalCreateSubscription(data, actions) {
      // https://developer.paypal.com/docs/subscriptions/integrate/#create-the-subscription
      return await this.$store.dispatch('actionForm/commitPayPalPayment')
    },
    getFormErrorMessage(error) {
      const i18nPath = `action.panels.FUNDRAISER_PAYMENT.form.errors.${error.code}`
      // Do we have a translation for this error code?
      if (this.$te(i18nPath, this.locale)) {
        return this.$t(i18nPath, this.locale)
      }
      // Otherwise, return the default error message from the provider
      return error.message
    }
  }
}
</script>

<style lang="scss" scoped>
.back-btn-container {
  position: absolute;
  left: 8px;
  top: 0;
}

::v-deep {
  .StripeElement {
    border-color: rgba(76, 76, 76, 0.41) !important;

    &.StripeElement--focus {
      border-color: rgb(76, 76, 76) !important;
    }
  }

  // properly hide the "hidden" input below the stripe element so it doesn't add unwanted margins
  button.hide {
    display: none;
  }
}

.paypal-svg {
  width: 20px;
  height: 20px;
  & > svg {
    fill: #04a7cb;
    width: 100%;
    height: 100%;
  }
  &.selected {
    & > svg {
      fill: white;
    }
  }
}

.fade-enter-active {
  transition: opacity 1s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fade-leave-to {
  display: none;
}

.paypal-wrap {
  &.disabled {
    position: relative;
    margin-bottom: -8px;
    cursor: not-allowed;
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: white;
      opacity: 0.3;
      z-index: 1000;
    }
  }
}
</style>
